/**
 * WebGL2 image renderer. The image is split into tiles (texture size limit),
 * stored as RGBA8 (colour view) and R32F (measurement plane). Window/level,
 * log scaling, colormap lookup and the saturation overlay run in the fragment
 * shader, so display changes are instantaneous even at 20+ megapixels.
 */
import type { View } from './transform';

const VS = `#version 300 es
in vec2 a_pos;
uniform vec4 u_rect;   // tile x, y, w, h in image pixels
uniform vec3 u_view;   // scale, tx, ty (CSS px)
uniform vec2 u_css;    // viewport size in CSS px
out vec2 v_uv;
void main() {
  vec2 img = u_rect.xy + a_pos * u_rect.zw;
  vec2 css = img * u_view.x + u_view.yz;
  vec2 clip = vec2(css.x / u_css.x * 2.0 - 1.0, 1.0 - css.y / u_css.y * 2.0);
  gl_Position = vec4(clip, 0.0, 1.0);
  v_uv = a_pos;
}`;

const FS = `#version 300 es
precision highp float;
uniform sampler2D u_color;
uniform highp sampler2D u_plane;
uniform sampler2D u_clip;
uniform sampler2D u_lut;
uniform int u_mode;        // 0 colour, 1 grey, 2 false colour
uniform float u_lo;
uniform float u_hi;
uniform bool u_log;
uniform bool u_showClip;
in vec2 v_uv;
out vec4 o;
void main() {
  vec3 c;
  if (u_mode == 0) {
    c = texture(u_color, v_uv).rgb;
  } else {
    float v = texture(u_plane, v_uv).r;
    if (isnan(v) || isinf(v)) {
      // Invalid values: dark magenta checker.
      vec2 g = floor(gl_FragCoord.xy / 4.0);
      c = mod(g.x + g.y, 2.0) < 1.0 ? vec3(0.35, 0.0, 0.35) : vec3(0.18, 0.0, 0.18);
    } else {
      float t;
      if (u_log) t = (log(max(v, 1e-30)) - log(u_lo)) / (log(u_hi) - log(u_lo));
      else t = (v - u_lo) / (u_hi - u_lo);
      t = clamp(t, 0.0, 1.0);
      if (u_mode == 1) c = vec3(t);
      else c = texture(u_lut, vec2(t * (255.0 / 256.0) + 0.5 / 256.0, 0.5)).rgb;
    }
  }
  if (u_showClip) {
    float m = texture(u_clip, v_uv).r * 255.0;
    if (m > 0.5 && m < 1.5) c = mix(c, vec3(1.0, 0.1, 0.1), 0.85);
    else if (m > 1.5 && m < 2.5) c = mix(c, vec3(0.1, 0.4, 1.0), 0.85);
    else if (m > 2.5) c = mix(c, vec3(1.0, 0.1, 1.0), 0.85);
  }
  o = vec4(c, 1.0);
}`;

interface Tile {
  x: number;
  y: number;
  w: number;
  h: number;
  color: WebGLTexture | null;
  plane: WebGLTexture | null;
  clip: WebGLTexture | null;
}

export interface RenderParams {
  mode: 0 | 1 | 2;
  lo: number;
  hi: number;
  log: boolean;
  showClip: boolean;
  smooth: boolean;
  cssW: number;
  cssH: number;
  dpr: number;
}

export class ImageRenderer {
  readonly gl: WebGL2RenderingContext;
  private prog: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private tiles: Tile[] = [];
  private lut: WebGLTexture;
  private dummy: WebGLTexture;
  private video: HTMLVideoElement | null = null;
  private videoTex: WebGLTexture | null = null;
  private loc: Record<string, WebGLUniformLocation | null> = {};
  private floatLinear: boolean;
  readonly tileSize: number;
  width = 0;
  height = 0;
  hasColor = false;
  hasPlane = false;

  constructor(private canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', { antialias: false, premultipliedAlpha: false, preserveDrawingBuffer: true });
    if (!gl) throw new Error('WebGL2 is not available in this browser');
    this.gl = gl;
    this.floatLinear = !!gl.getExtension('OES_texture_float_linear');
    this.tileSize = Math.min(4096, gl.getParameter(gl.MAX_TEXTURE_SIZE) as number);
    this.prog = this.link();
    for (const u of ['u_rect', 'u_view', 'u_css', 'u_color', 'u_plane', 'u_clip', 'u_lut', 'u_mode', 'u_lo', 'u_hi', 'u_log', 'u_showClip'])
      this.loc[u] = gl.getUniformLocation(this.prog, u);
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
    const a = gl.getAttribLocation(this.prog, 'a_pos');
    gl.enableVertexAttribArray(a);
    gl.vertexAttribPointer(a, 2, gl.FLOAT, false, 0, 0);
    this.lut = this.makeTex();
    this.dummy = this.makeTex();
    gl.bindTexture(gl.TEXTURE_2D, this.dummy);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));
  }

  private link(): WebGLProgram {
    const gl = this.gl;
    const sh = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? 'shader error');
      return s;
    };
    const p = gl.createProgram()!;
    gl.attachShader(p, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(p, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) ?? 'link error');
    return p;
  }

  private makeTex(): WebGLTexture {
    const gl = this.gl;
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return t;
  }

  /** Re-tile for a new image size, deleting old textures. */
  private ensureTiles(w: number, h: number) {
    if (w === this.width && h === this.height && this.tiles.length) return;
    const gl = this.gl;
    for (const t of this.tiles) [t.color, t.plane, t.clip].forEach((x) => x && gl.deleteTexture(x));
    this.tiles = [];
    const T = this.tileSize;
    for (let y = 0; y < h; y += T)
      for (let x = 0; x < w; x += T)
        this.tiles.push({ x, y, w: Math.min(T, w - x), h: Math.min(T, h - y), color: null, plane: null, clip: null });
    this.width = w;
    this.height = h;
    this.hasColor = false;
    this.hasPlane = false;
  }

  private upload(
    kind: 'color' | 'plane' | 'clip',
    w: number,
    h: number,
    data: ArrayBufferView,
    internal: number,
    format: number,
    type: number,
  ) {
    const gl = this.gl;
    this.ensureTiles(w, h);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_ROW_LENGTH, w);
    for (const t of this.tiles) {
      if (!t[kind]) t[kind] = this.makeTex();
      gl.bindTexture(gl.TEXTURE_2D, t[kind]);
      gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, t.x);
      gl.pixelStorei(gl.UNPACK_SKIP_ROWS, t.y);
      gl.texImage2D(gl.TEXTURE_2D, 0, internal, t.w, t.h, 0, format, type, data);
    }
    gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
    gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0);
    gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0);
  }

  setColor(rgba: Uint8Array, w: number, h: number) {
    const gl = this.gl;
    this.upload('color', w, h, rgba, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE);
    this.hasColor = true;
  }

  setPlane(data: Float32Array, w: number, h: number) {
    const gl = this.gl;
    this.upload('plane', w, h, data, gl.R32F, gl.RED, gl.FLOAT);
    this.hasPlane = true;
  }

  setClip(mask: Uint8Array | null, w: number, h: number) {
    const gl = this.gl;
    const m = mask ?? new Uint8Array(w * h);
    this.upload('clip', w, h, m, gl.R8, gl.RED, gl.UNSIGNED_BYTE);
  }

  /** 256-entry RGB lookup table. */
  setLut(rgb: Uint8Array) {
    const gl = this.gl;
    const rgba = new Uint8Array(256 * 4);
    for (let i = 0; i < 256; i++) {
      rgba[i * 4] = rgb[i * 3];
      rgba[i * 4 + 1] = rgb[i * 3 + 1];
      rgba[i * 4 + 2] = rgb[i * 3 + 2];
      rgba[i * 4 + 3] = 255;
    }
    gl.bindTexture(gl.TEXTURE_2D, this.lut);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, 256, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, rgba);
  }

  setVideo(video: HTMLVideoElement | null) {
    this.video = video;
    if (video && !this.videoTex) this.videoTex = this.makeTex();
  }

  clearImage() {
    const gl = this.gl;
    for (const t of this.tiles) [t.color, t.plane, t.clip].forEach((x) => x && gl.deleteTexture(x));
    this.tiles = [];
    this.width = this.height = 0;
    this.hasColor = this.hasPlane = false;
  }

  render(view: View, p: RenderParams) {
    const gl = this.gl;
    const W = Math.max(1, Math.round(p.cssW * p.dpr));
    const H = Math.max(1, Math.round(p.cssH * p.dpr));
    if (this.canvas.width !== W || this.canvas.height !== H) {
      this.canvas.width = W;
      this.canvas.height = H;
    }
    gl.viewport(0, 0, W, H);
    gl.clearColor(0.075, 0.08, 0.09, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.prog);
    gl.bindVertexArray(this.vao);
    gl.uniform3f(this.loc.u_view, view.scale, view.tx, view.ty);
    gl.uniform2f(this.loc.u_css, p.cssW, p.cssH);
    gl.uniform1f(this.loc.u_lo, p.lo);
    gl.uniform1f(this.loc.u_hi, p.hi > p.lo ? p.hi : p.lo + 1e-12);
    gl.uniform1i(this.loc.u_log, p.log ? 1 : 0);
    gl.uniform1i(this.loc.u_color, 0);
    gl.uniform1i(this.loc.u_plane, 1);
    gl.uniform1i(this.loc.u_clip, 2);
    gl.uniform1i(this.loc.u_lut, 3);
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, this.lut);

    const minify = view.scale < 1 && p.smooth;
    if (this.video && this.videoTex && this.video.readyState >= 2 && this.video.videoWidth) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.videoTex);
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, this.video);
      const f = minify ? gl.LINEAR : gl.NEAREST;
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, f);
      gl.uniform1i(this.loc.u_mode, 0);
      gl.uniform1i(this.loc.u_showClip, 0);
      gl.uniform4f(this.loc.u_rect, 0, 0, this.video.videoWidth, this.video.videoHeight);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      return;
    }
    const mode = p.mode === 0 && !this.hasColor ? 1 : p.mode;
    gl.uniform1i(this.loc.u_mode, mode);
    for (const t of this.tiles) {
      const sx0 = t.x * view.scale + view.tx;
      const sy0 = t.y * view.scale + view.ty;
      if (sx0 > p.cssW || sy0 > p.cssH || sx0 + t.w * view.scale < 0 || sy0 + t.h * view.scale < 0) continue;
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, t.color ?? this.dummy);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, minify ? gl.LINEAR : gl.NEAREST);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, t.plane ?? this.dummy);
      if (t.plane) gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, minify && this.floatLinear ? gl.LINEAR : gl.NEAREST);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, t.clip ?? this.dummy);
      gl.uniform1i(this.loc.u_showClip, p.showClip && t.clip ? 1 : 0);
      gl.uniform4f(this.loc.u_rect, t.x, t.y, t.w, t.h);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
  }

  dispose() {
    this.clearImage();
    const gl = this.gl;
    gl.deleteTexture(this.lut);
    gl.deleteTexture(this.dummy);
    if (this.videoTex) gl.deleteTexture(this.videoTex);
    gl.deleteProgram(this.prog);
  }
}
